// Frozen contract (Obsidian oads-discount/contracts/thanos-review-v1.md, HUB-448):
// GET /checkout/review-offer/eligibility response shape.
//
// HUB-466 (epic HUB-440 phase 2): extended for the two-program discount —
// `program` and top-level `discount_percent` are new fields, kept
// byte-identical to the server's types
// (oppizi-api src/billing/application/services/ReviewDiscountPrograms.ts's
// `REVIEW_DISCOUNT_PROGRAMS` and
// src/billing/application/useCases/checkout/GetReviewOfferEligibilityUseCase.ts's
// `EligibilityReason`/`GetReviewOfferEligibilityResponse`) — the two repos
// have drifted before (phase 1) and it caused a silent bug.

export type ReviewOfferProgram = "CHECKOUT_5" | "POST_CAMPAIGN_10";

/**
 * Defensive-only fallbacks for the brief window before the first eligibility
 * fetch resolves. Never rendered in practice: every state that shows
 * percent-bearing copy (S1, S4) only does so once `eligibility`/`entitlement`
 * data has actually loaded (see useReviewOfferState.ts). Mirrors the server's
 * `DEFAULT_REVIEW_DISCOUNT_PROGRAM` (CHECKOUT_5, 5%) so a fallback render
 * would be wire-consistent with phase 1's HTTP-boundary default even if one
 * were ever momentarily visible.
 */
export const DEFAULT_REVIEW_OFFER_PROGRAM: ReviewOfferProgram = "CHECKOUT_5";
export const DEFAULT_REVIEW_OFFER_PERCENT = 5;

export type ReviewOfferEligibilityReason =
  | "ok"
  | "flag_off"
  | "already_paid"
  // HUB-464/HUB-466: POST_CAMPAIGN_10's has-submitted-a-campaign rule failed.
  | "no_submitted_campaign"
  | "entitlement_active"
  | "entitlement_redeemed"
  | "entitlement_expired"
  | "service_error";

export type ReviewOfferEntitlementStatus = "active" | "redeemed" | "expired";

export type ReviewOfferEntitlement = {
  status: ReviewOfferEntitlementStatus;
  /** Which program granted this entitlement — drives which copy set/percent to render. */
  program: ReviewOfferProgram;
  review_id: string;
  expires_at: string;
  /** Read from the entitlement row, never the program registry — see server-side ReviewDiscountPrograms.ts. */
  discount_percent: number;
};

export type ReviewOfferEligibilityResponse = {
  eligible: boolean;
  reason: ReviewOfferEligibilityReason;
  /** The program this answer is about — echoed so callers never guess (HUB-464). */
  program: ReviewOfferProgram;
  /** What the client would get if eligible, or what they hold if not. */
  discount_percent: number;
  entitlement: ReviewOfferEntitlement | null;
};

// DOM event back-channel from the Thanos widget (contract §1). Optimistic UI
// only — the webhook (server-side) is the source of truth.
export type ThanosReviewSubmittedDetail = {
  review_id: string;
  rating: number;
  submitted_at: string;
};

export type ThanosReviewErrorDetail = {
  code: string;
  message: string;
};

// Row states per frontend-spec.md. B1/HUB-453 implements S1-S4/S7/S8;
// S5/S6 (coupon exclusivity + swap dialog) belong to B2/HUB-454.
export type ReviewOfferRowState = "S1" | "S2" | "S3" | "S4" | "S7" | "S8";
